import { useMemo } from "react";
import { formatAmount, formatPlain, parseAmount } from "@/money";
import { useI18n } from "./LocaleProvider";
import type { Locale } from "./locales";

/**
 * Dates, in the shape the reader expects them.
 *
 * Every date Kontora holds is a calendar date — `booked_date` is a day, not an
 * instant (`model.md` §1.5) — so these take the ISO string and never a Date
 * built from it. `new Date("2026-03-14")` is midnight UTC, which in a timezone
 * west of London is the 13th, and a booking that moves a day depending on where
 * somebody is standing is a bug that only appears for some of the people some of
 * the time.
 */
function parts(iso: string): { year: string; month: string; day: string } | undefined {
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return undefined;
  return { year, month, day };
}

/** A date as this locale writes it: `14.03.2026` or `14/03/2026`. */
export function formatDate(iso: string, locale: Locale): string {
  const date = parts(iso);
  if (!date) return iso;
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(Date.UTC(Number(date.year), Number(date.month) - 1, Number(date.day)));
}

/** A month with its name: `März 2026` or `March 2026`. */
export function formatMonth(iso: string, locale: Locale): string {
  const date = parts(iso);
  if (!date) return iso;
  return new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(Date.UTC(Number(date.year), Number(date.month) - 1, 1));
}

/** The abbreviated month a chart axis has room for: `Mär` or `Mar`. */
export function formatShortMonth(iso: string, locale: Locale): string {
  const date = parts(iso);
  if (!date) return iso;
  return new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" }).format(
    Date.UTC(Number(date.year), Number(date.month) - 1, 1),
  );
}

/** A weekday's name, from its ISO number (1 = Monday): `Montag` or `Monday`. */
export function formatWeekday(isoWeekday: number, locale: Locale): string {
  // 1 January 2024 was a Monday, so day n of that week is weekday n.
  return new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" }).format(
    Date.UTC(2024, 0, isoWeekday),
  );
}

/**
 * A day in a month with no year, as a yearly rule is described: `15.03.` or
 * `15/03`. The day is clamped to the month the way the schedule clamps it, so
 * "the 31st" of February reads as the 29th rather than rolling into March.
 */
export function formatDayMonth(day: number, month: number, locale: Locale): string {
  // A leap year, so the 29th of February is available to clamp to.
  const last = new Date(Date.UTC(2000, month, 0)).getUTCDate();
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", timeZone: "UTC" })
    .format(Date.UTC(2000, month - 1, Math.min(day, last)));
}

/**
 * A share, given in basis points, as this locale writes a percentage.
 *
 * Basis points rather than a fraction because that is how the server apportions
 * a whole into integer parts — 2929 is 29.29% — and converting to a float here
 * only to hand it straight to Intl keeps the arithmetic integer everywhere it
 * could round.
 *
 * One decimal place, and only when it is not a whole number: "29,3 %" carries
 * the information, "29,29 %" carries a precision the reader has no use for, and
 * "12 %" beats "12,0 %" beside eleven other rows.
 */
export function formatShare(basisPoints: number, locale: Locale): string {
  const percent = basisPoints / 100;
  return new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: Number.isInteger(percent) ? 0 : 1,
  }).format(basisPoints / 10000);
}

/**
 * Everything a screen needs to render numbers and dates, bound to the language
 * it is being read in.
 *
 * The alternative — passing the locale to each call — is what the module-level
 * functions are for, and what tests use. Components get this, because a screen
 * that formatted one figure in the reader's locale and the next in somebody
 * else's would be worse than either.
 */
export function useFormat() {
  const { locale } = useI18n();

  return useMemo(
    () => ({
      locale,
      amount: (minor: number, currency: string) => formatAmount(minor, currency, locale),
      plain: (minor: number, currency: string) => formatPlain(minor, currency, locale),
      parse: (text: string, currency: string) => parseAmount(text, currency, locale),
      date: (iso: string) => formatDate(iso, locale),
      month: (iso: string) => formatMonth(iso, locale),
      shortMonth: (iso: string) => formatShortMonth(iso, locale),
      share: (basisPoints: number) => formatShare(basisPoints, locale),
      weekday: (isoWeekday: number) => formatWeekday(isoWeekday, locale),
      dayMonth: (day: number, month: number) => formatDayMonth(day, month, locale),
    }),
    [locale],
  );
}
